import { useState } from "react";
import { Megaphone, Loader2, Copy, Check, Sparkles, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "@/lib/locationContext";
import { toast } from "sonner";

const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";

export default function PreShiftBriefing() {
  const { activeLocation } = useLocation();
  const [briefing, setBriefing] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null);

  const generate = async () => {
    setLoading(true);
    setCopied(false);
    try {
      const { data, error } = await supabase.functions.invoke("preshift-briefing", {
        body: { locationId: activeLocation?.id ?? null },
      });
      if (error) {
        const msg = (error as any)?.message ?? "Couldn't generate briefing";
        if (msg.includes("429")) toast.error("Rate limit hit. Try again in a moment.");
        else if (msg.includes("402")) toast.error("AI credits exhausted. Top up in workspace settings.");
        else toast.error(msg);
        return;
      }
      if ((data as any)?.error) {
        toast.error((data as any).error);
        return;
      }
      setBriefing((data as any)?.briefing ?? "");
      setGeneratedAt(new Date());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't generate briefing");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!briefing) return;
    try {
      await navigator.clipboard.writeText(briefing);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${BRAND_ORANGE}1a` }}
        >
          <Megaphone className="w-4 h-4" style={{ color: BRAND_ORANGE }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg font-medium" style={{ color: BRAND_DARK }}>
            Pre-shift Briefing
          </h2>
          <p className="text-xs text-muted-foreground">
            AI-generated daily talking points for your team
          </p>
        </div>
        {briefing && (
          <Button variant="outline" size="sm" onClick={generate} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
            Regenerate
          </Button>
        )}
      </div>

      {!briefing && !loading && (
        <div className="text-center py-8">
          <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: BRAND_ORANGE }} />
          <p className="text-sm text-muted-foreground mb-4">
            Pulls yesterday's revenue, your latest survival score, and your biggest challenge to write a 6-line briefing you can share with staff.
          </p>
          <Button
            onClick={generate}
            className="gap-2"
            style={{ backgroundColor: BRAND_ORANGE, color: "white" }}
          >
            <Sparkles className="w-4 h-4" />
            Generate Today's Briefing
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Drafting your briefing…
        </div>
      )}

      {briefing && !loading && (
        <div>
          <div
            className="rounded-xl p-4 whitespace-pre-wrap text-sm leading-relaxed"
            style={{ backgroundColor: "#fafaf7", color: BRAND_DARK }}
          >
            {briefing}
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-muted-foreground">
              {generatedAt ? `Generated ${generatedAt.toLocaleTimeString()}` : ""}
            </span>
            <Button variant="outline" size="sm" onClick={copy} className="gap-2">
              {copied ? <Check className="w-4 h-4" style={{ color: BRAND_GREEN }} /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}